/**
 * Drops a first word every side shares — Vitalli's four are all "House
 * something", and repeating it in a list costs the width the rest of the row
 * needs. Only when every side shares it, so Society's teams, which have nothing
 * in common, are left exactly as they are.
 */
export function trimSharedPrefix(names: string[]): (name: string) => string {
  if (names.length < 2) return (n) => n;
  const [first] = names[0].split(' ');
  const shared =
    first.length > 0 &&
    names.every((n) => {
      const parts = n.split(' ');
      return parts.length > 1 && parts[0] === first;
    });
  return shared ? (n) => n.slice(first.length + 1) : (n) => n;
}

/**
 * A name in the possessive.
 *
 * "Toro Titans's new players" is what naive concatenation produces, and four of
 * Society's twelve sides end in s — Toro Titans, Miss Hits, Net-Flicks and Kiss
 * My Aces — so it is not an edge case, it is a third of the league.
 *
 * A curly apostrophe, matching the rest of the interface. The plural rule only:
 * a singular name ending in s ("Jones") takes 's in most style guides, but
 * these are team names, which are plural by nature.
 */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`;
}

/**
 * A player's name as a scoreboard across a room can carry it: "M. Ebrahim".
 *
 * THE VENUE BOARD RUNS OUT OF ROOM THE MOMENT THE PAIRS ARE IN. Six fixtures,
 * three pairs each, four names to a row — set in full, "Mohammed Irfaan Ebrahim
 * & Zaeem Laher" takes three lines, the cards grow past their own borders and
 * the bottom row writes over the standings. Measured on the real board: content
 * reaching 888px where the table begins at 798.
 *
 * Initial and surname is what every scoreboard does, and it is the difference
 * between a row of three lines and a row of one.
 *
 * THE LAST WORD IS THE SURNAME, and everything before it becomes one initial —
 * so "Mohammed Irfaan Ebrahim" is "M. Ebrahim" rather than "M. I. Ebrahim",
 * which is longer and reads worse at ten paces. A single-word name is left
 * alone: there is nothing to shorten and an initial of nothing is a full stop.
 */
export function courtName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name.trim();
  const surname = parts[parts.length - 1];
  const initial = [...parts[0]][0];
  return initial ? `${initial}. ${surname}` : surname;
}
